import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, fmtDate } from '../lib/api.js';

export default function MyDay() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  function reload() {
    api.get('/api/myday').then(setData).catch(e => setErr(e.message));
  }
  useEffect(reload, []);

  if (err) return <ErrorBox msg={err} />;
  if (!data) return <Loading />;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-serif text-olive-900">Good {timeOfDay()}, {data.user.name.split(' ')[0]}.</h1>
        <p className="text-sm text-ink-700/70">{fmtDate(data.date)}</p>
      </header>

      <Section title="Chores" empty="No chores assigned to you today.">
        {data.chores.length > 0 && (
          <ul className="space-y-2">
            {data.chores.map(c => <ChoreCard key={c.id} c={c} reload={reload} />)}
          </ul>
        )}
      </Section>

      <Section title="PM tasks due" empty="Nothing due. Equipment is current.">
        {data.pm_due.length > 0 && (
          <ul className="space-y-2">
            {data.pm_due.map(t => <PmDueRow key={t.id} t={t} reload={reload} />)}
          </ul>
        )}
      </Section>

      <EggSummary />

      <div className="grid grid-cols-2 gap-3">
        <Link to="/receipts/new" className="card hover:bg-olive-50 transition flex flex-col items-center text-center py-6">
          <div className="text-3xl mb-2">📷</div>
          <div className="font-medium text-olive-900">Log Receipt</div>
          <div className="text-xs text-ink-700/60 mt-1">Snap or enter manually</div>
        </Link>
        <Link to="/income/new" className="card hover:bg-olive-50 transition flex flex-col items-center text-center py-6">
          <div className="text-3xl mb-2">💰</div>
          <div className="font-medium text-olive-900">Log Income</div>
          <div className="text-xs text-ink-700/60 mt-1">Cash sale, eggs, beef…</div>
        </Link>
      </div>
    </div>
  );
}

function Section({ title, children, empty }) {
  return (
    <section>
      <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-700/70 mb-2">{title}</h2>
      {children || <p className="text-sm text-ink-700/60 italic">{empty}</p>}
    </section>
  );
}

function CadenceBadge({ type }) {
  const map = {
    daily: { label: 'Daily',   cls: 'bg-olive-100 text-olive-900' },
    daily_ampm: { label: 'AM+PM', cls: 'bg-gold-500/15 text-gold-600' },
    weekday: { label: 'Wkdy',  cls: 'bg-olive-100 text-olive-900' },
    weekly: { label: 'Wkly',   cls: 'bg-olive-100 text-olive-900' },
    monthly: { label: 'Mthly', cls: 'bg-olive-100 text-olive-900' },
  };
  const m = map[type] || { label: type, cls: 'bg-ink-700/10 text-ink-700' };
  return <span className={`pill ${m.cls}`}>{m.label}</span>;
}

function EggSummary() {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get('/api/chickens/eggs/summary?days=7').then(setD).catch(() => {});
  }, []);
  if (!d || (d.today_eggs === 0 && d.total_eggs === 0)) return null;
  return (
    <Link to="/chickens" className="card hover:bg-olive-50 transition flex items-center gap-3 px-4 py-3">
      <div className="text-3xl">🥚</div>
      <div className="flex-1">
        <div className="font-medium text-ink-900">Today: {d.today_eggs} egg{d.today_eggs === 1 ? '' : 's'}</div>
        <div className="text-xs text-ink-700/60">7-day total {d.total_eggs} · avg {d.avg_per_day}/day</div>
      </div>
      <div className="text-ink-700/40">›</div>
    </Link>
  );
}

function PmDueRow({ t, reload }) {
  const [busy, setBusy] = useState(false);
  async function markDone() {
    if (!confirm(`Mark "${t.description}" done at ${t.hour_meter ?? 'current'}hr?`)) return;
    setBusy(true);
    try {
      await api.post(`/api/pm-tasks/${t.id}/done`, { hour_meter: t.hour_meter ?? null });
      reload();
    } finally { setBusy(false); }
  }
  return (
    <li className="card flex items-start gap-3">
      <span className="pill-due">DUE</span>
      <div className="flex-1">
        <div className="font-medium text-ink-900">{t.description}</div>
        <div className="text-sm text-ink-700/70 mt-0.5">
          <Link to={`/equipment/${t.equipment_id}`} className="underline-offset-2 hover:underline">
            {t.make} {t.model}
          </Link>
          {t.hour_meter != null && <span> · {t.hour_meter}hr</span>}
          {t.interval_hours && <span> · every {t.interval_hours}hr</span>}
        </div>
        {t.last_done_at && <div className="text-xs text-ink-700/60 mt-1">Last: {fmtDate(t.last_done_at)}</div>}
      </div>
      <button onClick={markDone} disabled={busy} className="btn-primary text-sm py-1 px-3">
        {busy ? '…' : 'Mark Done'}
      </button>
    </li>
  );
}

function ChoreCard({ c, reload }) {
  const isAmpm = c.cadence_type === 'daily_ampm';
  const done = c.done_today || {};

  async function markDone(slot) {
    await api.post(`/api/chores/${c.id}/done`, slot ? { ampm_slot: slot } : {});
    reload();
  }
  async function undo(slot) {
    await api.delete(`/api/chores/${c.id}/done${slot ? `?ampm_slot=${slot}` : ''}`);
    reload();
  }

  if (isAmpm) {
    const amDone = !!done.am;
    const pmDone = !!done.pm;
    return (
      <li className="card flex items-start gap-3">
        <CadenceBadge type={c.cadence_type} />
        <div className="flex-1">
          <div className="font-medium text-ink-900">{c.name}</div>
          {c.description && <div className="text-sm text-ink-700/70 mt-0.5">{c.description}</div>}
          {c.segment_name && <div className="text-xs text-ink-700/60 mt-1">Segment: {c.segment_name}</div>}
        </div>
        <div className="flex flex-col gap-1">
          <SlotBtn label="AM" done={amDone} onDone={() => markDone('am')} onUndo={() => undo('am')} />
          <SlotBtn label="PM" done={pmDone} onDone={() => markDone('pm')} onUndo={() => undo('pm')} />
        </div>
      </li>
    );
  }

  const isDone = !!done.any;
  return (
    <li className={`card flex items-start gap-3 ${isDone ? 'opacity-60' : ''}`}>
      <CadenceBadge type={c.cadence_type} />
      <div className="flex-1">
        <div className={`font-medium text-ink-900 ${isDone ? 'line-through' : ''}`}>{c.name}</div>
        {c.description && <div className="text-sm text-ink-700/70 mt-0.5">{c.description}</div>}
        {c.segment_name && <div className="text-xs text-ink-700/60 mt-1">Segment: {c.segment_name}</div>}
      </div>
      {isDone ? (
        <button onClick={() => undo()} className="text-xs text-ink-700/60 hover:underline">undo</button>
      ) : (
        <button onClick={() => markDone()} className="btn-primary text-sm py-1 px-3">Mark Done</button>
      )}
    </li>
  );
}

function SlotBtn({ label, done, onDone, onUndo }) {
  if (done) {
    return (
      <button onClick={onUndo} className="text-xs text-ink-700/60 hover:underline">
        ✓ {label} done — undo
      </button>
    );
  }
  return (
    <button onClick={onDone} className="btn-primary text-xs py-1 px-2">{label} done</button>
  );
}

function Loading() { return <div className="text-ink-700/60 italic">Loading…</div>; }
function ErrorBox({ msg }) { return <div className="bg-rust-500/10 text-rust-700 rounded-md px-3 py-2 text-sm">{msg}</div>; }

function timeOfDay() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}
